import React from "react";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { triggerHaptic } from "@/utilities/haptics";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useAcceptedOdds } from "@/features/betslip/hooks/useAcceptedOdds";
import {
  useRemoveSelection,
  useRemoveSemLeg,
} from "@/features/betslip/hooks/useBetslipActions";
import type { SemSelection } from "@/features/betslip/types";
import { isSemSrm } from "@/features/betslip/utils/typeGuards";
import {
  sortSemLegs,
  toOutcomeType,
} from "@/features/betslip/utils/selectionHelpers";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { SingleFooter } from "@/features/betslip/components/cards/SingleFooter";
import { SelectionAlert } from "@/features/betslip/components/parts/SelectionAlert";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { ComboGroup } from "@/features/betslip/components/parts/ComboGroup";
import { OddsDisplay } from "@/features/betslip/components/parts/OddsDisplay";
import { SemLegAlert } from "@/features/betslip/components/parts/SemLegAlert";
import { PromoPills } from "@/features/betslip/components/parts/tokens/PromoPills";
import { MysteryMultiHeader } from "@/features/sem-generator/components/MysteryMultiHeader";

type SemSelectionCardProps = {
  outcomeId: string;
  selection: SemSelection;
};

export const SemSelectionCard = ({
  outcomeId,
  selection,
}: SemSelectionCardProps) => {
  const isSrm = isSemSrm(selection);
  const title = useTitle(selection.sport.toLowerCase());
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const pendingOdds = useBetslipStore((s) => s.oddsChanges[outcomeId]);
  const acceptedOdds = useAcceptedOdds(outcomeId);
  const isLiveViewOnly = useBetslipStore((s) => s.liveViewOnly[outcomeId]);
  const removeSelection = useRemoveSelection();
  const removeSemLeg = useRemoveSemLeg();

  const legs = sortSemLegs(selection.legs ?? [], isSrm);
  const legCount = legs.length;
  const canRemoveLeg = legCount > 2;

  const handleRemove = () => {
    triggerHaptic.click();
    removeSelection(outcomeId);
  };

  const handleRemoveLeg = (legOutcomeId: string) => {
    triggerHaptic.click();
    removeSemLeg(outcomeId, legOutcomeId);
  };

  return (
    <BetCard
      testId={`selection-${outcomeId}`}
      alert={<SelectionAlert outcomeId={outcomeId} placement="card" />}
      banner={
        selection.mysteryMulti ? (
          <MysteryMultiHeader level={selection.mysteryMulti.level} />
        ) : null
      }
    >
      <BetCardHeader
        icon={<TitleIcon title={title} />}
        title={selection.eventName}
        clamp={<SelectionEventStatus selection={selection} />}
        rightSlot={
          <RemoveButton
            onClick={handleRemove}
            disabled={isPlacing}
            testId={`remove-${outcomeId}`}
          />
        }
        testId={`header-single-${outcomeId}`}
      />
      <SelectionAlert outcomeId={outcomeId} placement="outcome" />
      <ComboGroup
        variant={isSrm ? "srm" : "sgm"}
        label={`${legCount} Legs`}
        boldLabel={
          <>
            @{" "}
            <OddsDisplay
              odds={acceptedOdds ?? selection.odds}
              newOdds={pendingOdds}
              dash={isLiveViewOnly}
            />
          </>
        }
        testId={`sem-legs-${outcomeId}`}
      >
        <BetSpine padded>
          {legs.map((leg) => {
            const competitor = selection.competitors?.[leg.outcomeId];
            // SEM legs show only their market — the combined price lives in the
            // header ("X Legs @ odds"), not per leg.
            const marketLabel = leg.marketName;
            return (
              <React.Fragment key={`${leg.marketId}-${leg.outcomeId}`}>
                <SemLegAlert
                  semOutcomeId={outcomeId}
                  legOutcomeId={leg.outcomeId}
                />
                <BetRow
                  icon={
                    <OutcomeIcon
                      outcomeType={toOutcomeType(leg.outcomeType)}
                      competitor={competitor}
                      silksUrl={selection.silksUrl}
                      size={24}
                      reserveAvatarSpace
                    />
                  }
                  name={leg.name}
                  market={marketLabel}
                  rightSlot={
                    canRemoveLeg ? (
                      <RemoveButton
                        onClick={() => handleRemoveLeg(leg.outcomeId)}
                        disabled={isPlacing}
                        testId={`remove-sem-leg-${leg.outcomeId}`}
                      />
                    ) : null
                  }
                  testId={`bet-row-${leg.outcomeId}`}
                />
              </React.Fragment>
            );
          })}
        </BetSpine>
      </ComboGroup>
      <SingleFooter outcomeId={outcomeId} selection={selection} />
      <PromoPills
        outcomeIds={[outcomeId]}
        isMulti={false}
        selection={selection}
      />
    </BetCard>
  );
};
